import type {
  YmmApiResponse,
  YmmOnChangeApiResponse,
  ParsedYmmDefaultResponse,
  ParsedYmmOnChangeResponse,
  ParsedProductsApiResponse,
  ParsedProductItem,
  YmmField,
  YmmFieldOption,
  GetProductsApiResponse,
  ParsedCompatibleListResponse,
  YmmFitmentLabelApiResponse,
  ParsedFitmentLabelResponse,
} from './types';

/**
 * Helper function to parse HTML and extract fields
 */
function parseHtmlFields(html: string): YmmField[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const fields: YmmField[] = [];
  const selectElements = doc.querySelectorAll('select');

  selectElements.forEach((select) => {
    const name = select.getAttribute('name') || '';
    const id = select.getAttribute('id') || '';
    const required = select.hasAttribute('required');
    const tagPrefix = select.getAttribute('data-tag-prefix') || '';
    const title = select.getAttribute('title') || '';

    // Extract options
    const options: YmmFieldOption[] = [];
    const optionElements = select.querySelectorAll('option');

    optionElements.forEach((option) => {
      const value = option.getAttribute('value') || '';
      const text = option.textContent?.trim() || '';

      // Skip the placeholder option (value === '-1')
      if (value !== '-1' && text) {
        options.push({
          label: text,
          value: value,
        });
      }
    });

    // Determine label from title or placeholder option
    const placeholderOption = select.querySelector('option[value="-1"]');
    const label = placeholderOption?.textContent?.trim() || title || name;

    fields.push({
      name,
      id,
      label,
      required,
      tagPrefix,
      options,
      placeholder: placeholderOption?.textContent?.trim() || `Select ${label}`,
    });
  });

  return fields;
}

/**
 * Parse HTML string from YMM API default response and extract dropdown fields and options
 */
export function parseDefaultApiResponse(response: YmmApiResponse): ParsedYmmDefaultResponse {
  const fields = parseHtmlFields(response.ymmlist);

  return {
    config: {
      showYmmSelectedValues: response.showYmmSelectedValues,
      autoClickLastSearch: response.autoClickLastSearch,
      requiredFieldCount: response.requiredFieldCount,
      ymmhtmlClass: response.ymmhtmlClass,
      resultpage: response.resultpage,
    },
    fields,
  };
}

/**
 * Parse HTML string from YMM API onChange response and extract dropdown fields and options
 */
export function parseOnChangeApiResponse(response: YmmOnChangeApiResponse): ParsedYmmOnChangeResponse {
  const fields = parseHtmlFields(response.html);

  return {
    config: {
      showYmmSelectedValues: response.showYmmSelectedValues,
      autoClickLastSearch: response.autoClickLastSearch,
      requiredFieldCount: response.requiredFieldCount,
    },
    fields,
  };
}

/**
 * Parse product grid HTML (ul.productGrid > li.product > article.card) into structured product items.
 * Handles regular price, sale (Was/Now), MSRP, sale badge, and stock status.
 */
function parseProductGridHtml(html: string): ParsedProductItem[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const items: ParsedProductItem[] = [];
  const cards = doc.querySelectorAll('.productGrid .product .card.product-article');

  cards.forEach((article) => {
    const id =
      article.querySelector<HTMLElement>('[data-product-id]')?.getAttribute('data-product-id') ?? '';
    const titleLink = article.querySelector('.card-title a');
    const name = titleLink?.textContent?.trim() ?? '';
    const url = titleLink?.getAttribute('href') ?? article.querySelector('.prod-url-image')?.getAttribute('href') ?? '';
    const brandEl = article.querySelector('[data-test-info-type="brandName"]');
    const brand = brandEl?.textContent?.trim() ?? '';
    const priceSection = article.querySelector('[data-test-info-type="price"]');
    const msrpEl = priceSection?.querySelector('.price--rrp');
    const msrp = msrpEl?.textContent?.trim();
    const compareAtEl = priceSection?.querySelector('.price--non-sale');
    const compareAtPrice = compareAtEl?.textContent?.trim();
    // "Now" price: .price that is neither .price--rrp nor .price--non-sale
    const priceEls = priceSection?.querySelectorAll('.price') ?? [];
    let price = '';
    for (const el of priceEls) {
      const elClass = el as Element;
      if (!elClass.classList.contains('price--rrp') && !elClass.classList.contains('price--non-sale')) {
        price = elClass.textContent?.trim() ?? '';
        break;
      }
    }
    if (!price && !compareAtPrice) {
      const fallback = priceSection?.querySelector('.price:not(.price--rrp)');
      price = fallback?.textContent?.trim() ?? '';
    }
    const onSale =
      !!article.querySelector('.sale-flag-side, .sale-text') || !!compareAtPrice;
    const addToCartLink = article.querySelector('.card-figcaption-body a[href*="cart.php?action=add"]');
    const inStock = !!addToCartLink;
    const img = article.querySelector('.card-figure img');
    const imageUrl = img?.getAttribute('src') ?? undefined;
    const isDefaultImage =
      !!imageUrl && /ProductDefault|placeholder|default\.(gif|jpg|png)/i.test(imageUrl);
    const ratingEl = article.querySelector('[data-test-info-type="productRating"]');
    const fullStars = ratingEl?.querySelectorAll('.icon--ratingFull')?.length ?? 0;
    const rating = fullStars > 0 ? fullStars : undefined;

    items.push({
      id,
      name,
      url,
      brand,
      price,
      ...(msrp && { msrp }),
      ...(compareAtPrice && { compareAtPrice }),
      ...(onSale && { onSale: true }),
      ...(addToCartLink === null && { inStock: false }),
      ...(addToCartLink !== null && { inStock: true }),
      ...(rating !== undefined && { rating }),
      ...(isDefaultImage && { isDefaultImage: true }),
      ...(imageUrl && { imageUrl }),
    });
  });

  return items;
}

/**
 * Parse selected "products per page" value from response HTML (form#productsPerPage select).
 */
function parseProductsPerPageFromHtml(html: string): string | undefined {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const select = doc.querySelector<HTMLSelectElement>('#products_per_page, select[name="products_per_page"]');
  const selected = select?.options[select.selectedIndex];
  return selected?.getAttribute('value') ?? undefined;
}

/**
 * Parse full get_products API response into structured products list, filters, and options.
 */
export function parseProductsApiResponse(response: GetProductsApiResponse): ParsedProductsApiResponse {
  const products = parseProductGridHtml(response.html);
  const productsPerPage = parseProductsPerPageFromHtml(response.html);

  return {
    products,
    total: response.total,
    length: response.length,
    filterHtml: response.filter_html,
    customFacets: response.customFacets,
    resultPageOptions: response.resultPageOptions,
    ...(productsPerPage && { productsPerPage }),
    config: {
      showYmmSelectedValues: response.showYmmSelectedValues,
      autoClickLastSearch: response.autoClickLastSearch,
      requiredFieldCount: response.requiredFieldCount,
    },
  };
}

/**
 * Parse getcompatiblelist HTML into headers and rows (year, make, model).
 * Expects .ymm_table with thead th and tbody tr > td.
 */
export function parseCompatibleListHtml(html: string): ParsedCompatibleListResponse {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const table = doc.querySelector('.ymm_table, table.ymm_table');
  const headers: string[] = [];
  const rows: Array<{ year: string; make: string; model: string }> = [];

  if (!table) {
    return { headers, rows };
  }

  const ths = table.querySelectorAll('thead th');
  ths.forEach((th) => {
    const text = th.textContent?.trim() ?? '';
    if (text) headers.push(text);
  });

  const trs = table.querySelectorAll('tbody tr');
  trs.forEach((tr) => {
    const tds = tr.querySelectorAll('td');
    const cells = Array.from(tds).map((td) => td.textContent?.trim() ?? '');
    if (cells.length >= 3) {
      rows.push({
        year: cells[0] ?? '',
        make: cells[1] ?? '',
        model: cells[2] ?? '',
      });
    }
  });

  return { headers, rows };
}

export function parseFitmentLabelHtml(response: YmmFitmentLabelApiResponse): ParsedFitmentLabelResponse {
  if (!response.fitmentData?.fitment_html) {
    return { // workaround to disable the fitment banner if there is no fitment html
      appIsDisabled: true,
      productFit: 'not-fit',
      labelFit: '',
      descriptionFit: '',
      labelDontFit: '',
      descriptionDontFit: '',
      disableForDontFit: false,
    };
  }

  const parse = new DOMParser();
  const doc = parse.parseFromString(response.fitmentData.fitment_html, 'text/html');

  const labelFit = doc.querySelector('.fit_text');
  const labelDontFit = doc.querySelector('.not_fit_text');
  const descriptionFit = doc.querySelector('.fit_desc');
  const descriptionDontFit = doc.querySelector('.not_fit_desc');

  return {
    appIsDisabled: false,
    productFit: response.productFit,
    labelFit: labelFit ? labelFit.textContent?.trim() : '',
    descriptionFit: descriptionFit ? descriptionFit.textContent?.trim() : '',
    labelDontFit: labelDontFit ? labelDontFit.textContent?.trim() : '',
    descriptionDontFit: descriptionDontFit ? descriptionDontFit.textContent?.trim() : '',
    disableForDontFit: response.textfield_settings.disable_textbox_field === "on" ? true : false,
  };
}